/* eslint-disable no-console */
import { useCallback, useEffect, useState } from 'react';
import { isSodimacRetail } from '@digital-retail/store-manager';
import { constants } from '../../config/constants';
import { CookieStorage } from '../CookieStorage';
import { httpService } from '../httpService';

const useUserSegments = (regionCode, userSegments, isUserLoggedIn, store) => {
  const [currentUserSegments, setCurrentUserSegments] = useState(userSegments);
  const getUserSegmentsFromCookie = () => {
    const userSegmentValue = CookieStorage.getCookie(constants.USER_SEGMENTS);
    const userSegmentDecode = CookieStorage.parseCookieValue(constants.USER_SEGMENTS, userSegmentValue);
    return userSegmentDecode || [];
  };

  const setUserSegmentsToCookie = useCallback(
    (segmentsData) => {
      CookieStorage.setCookie({
        name: constants.USER_SEGMENT_COOKIE,
        value: Buffer.from(JSON.stringify(segmentsData)).toString('base64'),
        regionCode,
        expiryDays: 1,
        store,
      });
    },
    [regionCode, store]
  );

  const getAccountOptions = () => {
    if (isSodimacRetail(store)) {
      return {
        accountPath: 'myaccount',
        commerceName: `sodimac-${regionCode}`,
      };
    }
    return {
      accountPath: 'account',
      commerceName: `marketplace-${regionCode}`,
    };
  };

  const fetchUserSegments = useCallback(async () => {
    try {
      const apiBaseUrl = window.location.origin;
      const userCookieId = CookieStorage.getCookie('USER_ID');
      const sessionId = CookieStorage.getCookie('sid');
      const { accountPath, commerceName } = getAccountOptions();
      const segmentsEndpoint = `${apiBaseUrl}/s/${accountPath}/v2/user/${userCookieId}/segments`;

      if (!userCookieId || !sessionId) {
        return;
      }

      const { error, data } = await httpService().get(segmentsEndpoint, {
        includeTracing: false,
        headers: {
          'x-channel-id': 'WEB',
          'x-ecomm-name': commerceName,
          Authorization: sessionId,
        },
      });

      if (error) {
        console.error('Error getting the last user segments:', error);
        return;
      }

      if (data && data?.data && data?.data?.user) {
        const { segments } = data.data.user;
        setUserSegmentsToCookie(segments);
        setCurrentUserSegments(segments);
      }
    } catch (err) {
      console.error('Error fetching user segments:', err);
    }
  }, [regionCode, setUserSegmentsToCookie]);

  useEffect(() => {
    if (isUserLoggedIn) {
      if (!userSegments?.length) {
        const cachedSegments = getUserSegmentsFromCookie();
        if (cachedSegments?.length) {
          setCurrentUserSegments(cachedSegments);
        } else {
          fetchUserSegments();
        }
      } else {
        setCurrentUserSegments(userSegments);
      }
    }
  }, [isUserLoggedIn, userSegments, fetchUserSegments]);

  return { currentUserSegments };
};

export { useUserSegments };
